import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';

import styles from './ToastProvider.module.scss';

type Variant = 'success' | 'error';

type ToastState = {
  open: boolean;
  message: string;
  variant: Variant;
};

type ToastContextValue = {
  showToast: (message: string, variant?: Variant) => void;
  hideToast: () => void;
};

export const ToastContext = createContext<ToastContextValue | null>(null);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: '',
    variant: 'success',
  });
  const timerRef = useRef<number | null>(null);

  const hideToast = useCallback(() => {
    setToast((t) => ({ ...t, open: false }));
  }, []);

  const showToast = useCallback(
    (message: string, variant: Variant = 'success') => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setToast({ open: true, message, variant });
      timerRef.current = window.setTimeout(() => {
        hideToast();
        timerRef.current = null;
      }, 5000);
    },
    [hideToast]
  );

  const value = useMemo(
    () => ({ showToast, hideToast }),
    [showToast, hideToast]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {ReactDOM.createPortal(
        <div
          role="status"
          aria-live="polite"
          className={`${styles.container} ${toast.open ? styles.enter : styles.leave}`}
        >
          <div
            className={`${styles.toast} ${toast.variant === 'error' ? styles.error : styles.success}`}
          >
            <span className={styles.message}>{toast.message}</span>
          </div>
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
};

export const useToastContext = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx)
    throw new Error('useToastContext must be used within <ToastProvider>');
  return ctx;
};
